/*
 * @ts-nocheck
 * Preventing TS checks with files presented in the video for a better presentation.
 */
import type { UIMessage } from 'ai';
import React, { type RefCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStore } from '@nanostores/react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { Workbench } from '~/components/workbench/Workbench.client';
import { RecentProjects } from '~/components/chat/RecentProjects';
import { ShowcasePrompts } from '~/components/chat/ShowcasePrompts';
import { HeroBackground } from '~/components/chat/HeroBackground';
import { workbenchStore } from '~/lib/stores/workbench';
import { fetchPreviewUrlWithRetry } from '~/components/workbench/preview-url';
import { StickToBottom, useStickToBottomContext } from '~/lib/hooks';
import { PROVIDER_LIST } from '~/lib/provider-list';
import { $accessToken } from '~/lib/stores/auth';
import { expoUrlAtom } from '~/lib/stores/qrCodeStore';
import { $wakingUp } from '~/lib/stores/sandbox';
import type { ActionAlert, LlmErrorAlertType } from '~/types/actions';
import type { ProgressAnnotation } from '~/types/context';
import type { DesignScheme } from '~/types/design-scheme';
import type { ModelInfo, ProviderInfo } from '~/types/model';
import type { BrochureAttachment } from '~/lib/pdf/brochure';
import type { PdfDocAttachment } from '~/lib/pdf/pdf-text';
import { classNames } from '~/utils/classNames';
import { requestComposerUpload, submitComposerFile } from '~/lib/stores/composerUpload';
import { runtime } from '~/lib/runtime';
import { $currentUser } from '~/lib/stores/auth';
import { streamingState } from '~/lib/stores/streaming';
import styles from './BaseChat.module.scss';
import ChatAlert from './ChatAlert';
import { ChatBox, type ProjectTabId } from './ChatBox';
import { Header } from '~/components/header/Header';
import { ExamplePrompts } from './ExamplePrompts';
import LlmErrorAlert from './LLMApiAlert';
import { Messages } from './Messages.client';
import ProgressCompilation from './ProgressCompilation';
import { SleepBanner } from './SleepBanner';
import { SandboxErrorBanner } from './SandboxErrorBanner';
import { BuildQueueBanner } from './BuildQueueBanner';
import { $isQueued } from '~/lib/stores/turn-queue';
import { BuildActivity } from './BuildActivity';
import { latestTaskUpdate } from './task-update';
import type { TaskItem } from '~/types/context';
import { SuggestionChips } from './SuggestionChips';
import { latestSuggestions } from './suggestion-chips';

const TEXTAREA_MIN_HEIGHT = 52;
// Hero composer default. Two comfortable lines — enough to signal "a brief, not
// a search box" without the empty input reading as a void. It still grows as
// the user types.
const TEXTAREA_MIN_HEIGHT_NEW = 62;

// DEV-155 readiness watchdog grace window. A fresh boot legitimately sits in
// 'starting' while the agent scaffolds + starts the dev server, so we only arm
// this AFTER generation ends — this is just buffer for the :5173 port event to
// propagate before we conclude the server genuinely never came up.
const STARTING_READINESS_TIMEOUT_MS = 45_000;

interface BaseChatProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement> | undefined;
  messageRef?: RefCallback<HTMLDivElement> | undefined;
  scrollRef?: RefCallback<HTMLDivElement> | undefined;
  showChat?: boolean;
  chatStarted?: boolean;
  /** A boot failed on the hero before chatStarted flipped — still show the retry banner. */
  sandboxBootFailed?: boolean;
  isStreaming?: boolean;
  onStreamingChange?: (streaming: boolean) => void;
  messages?: UIMessage[];
  description?: string;
  enhancingPrompt?: boolean;
  promptEnhanced?: boolean;
  enhanceError?: string | null;
  clearEnhanceError?: () => void;
  input?: string;
  model?: string;
  setModel?: (model: string) => void;
  provider?: ProviderInfo;
  setProvider?: (provider: ProviderInfo) => void;
  providerList?: ProviderInfo[];
  handleStop?: () => void;
  canResume?: boolean;
  handleResume?: () => void;
  sendMessage?: (event: React.UIEvent, messageInput?: string) => void;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enhancePrompt?: () => void;
  importChat?: (description: string, messages: UIMessage[]) => Promise<void>;
  exportChat?: () => void;
  uploadedFiles?: File[];
  setUploadedFiles?: (files: File[]) => void;
  imageDataList?: string[];
  setImageDataList?: (dataList: string[]) => void;
  actionAlert?: ActionAlert;
  clearAlert?: () => void;
  llmErrorAlert?: LlmErrorAlertType;
  clearLlmErrorAlert?: () => void;
  onRetryLlmError?: () => void;
  chatMode?: 'discuss' | 'build';
  setChatMode?: (mode: 'discuss' | 'build') => void;
  append?: (message: UIMessage) => void;
  designScheme?: DesignScheme;
  setDesignScheme?: (scheme: DesignScheme) => void;
  projectType?: ProjectTabId;
  setProjectType?: (type: ProjectTabId) => void;
  isStartingChat?: boolean;
  addToProject?: boolean[];
  onToggleAddToProject?: (index: number) => void;
  onRemoveFile?: (index: number) => void;
  brochure?: BrochureAttachment | null;
  onBrochureAttach?: (file: File) => void;
  onBrochureRemove?: () => void;
  pdfAttachments?: PdfDocAttachment[];
  onPdfAttach?: (file: File) => void;
  onPdfAttachRemove?: (file: File) => void;
  checkpoints?: Set<string>;
  onRestore?: (messageId: string) => void;
}

export const BaseChat = React.forwardRef<HTMLDivElement, BaseChatProps>(
  (
    {
      textareaRef,
      showChat = true,
      chatStarted = false,
      sandboxBootFailed = false,
      isStreaming = false,
      onStreamingChange,
      model,
      setModel,
      provider,
      setProvider,
      providerList,
      input = '',
      enhancingPrompt,
      enhanceError,
      clearEnhanceError,
      handleInputChange,

      // promptEnhanced,
      enhancePrompt,
      sendMessage,
      handleStop,
      canResume,
      handleResume,
      importChat: _importChat,
      exportChat,
      uploadedFiles = [],
      setUploadedFiles,
      imageDataList = [],
      setImageDataList,
      messages,
      actionAlert,
      clearAlert,
      llmErrorAlert,
      clearLlmErrorAlert,
      onRetryLlmError,
      chatMode,
      setChatMode,
      append,
      designScheme,
      setDesignScheme,
      projectType,
      setProjectType,
      isStartingChat,
      addToProject,
      onToggleAddToProject,
      onRemoveFile,
      brochure,
      onBrochureAttach,
      onBrochureRemove,
      pdfAttachments,
      onPdfAttach,
      onPdfAttachRemove,
      checkpoints,
      onRestore,
    },
    ref,
  ) => {
    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;
    const [modelList, setModelList] = useState<ModelInfo[]>([]);
    const [isModelSettingsCollapsed, setIsModelSettingsCollapsed] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
    const [, setTranscript] = useState('');

    // Voice errors render inline in the composer (next to the mic), not as toasts.
    const [voiceError, setVoiceError] = useState<string | null>(null);

    // A file is being dragged over the hero — shows the page-wide drop target.
    const [isDraggingFile, setIsDraggingFile] = useState(false);

    useEffect(() => {
      if (!voiceError) {
        return undefined;
      }

      const timeout = setTimeout(() => setVoiceError(null), 6000);

      return () => clearTimeout(timeout);
    }, [voiceError]);

    const [isModelLoading, setIsModelLoading] = useState<string | undefined>('all');

    // TODO: Wire up progress annotations from stream metadata
    const progressAnnotations: ProgressAnnotation[] = [];
    const sandboxStatus = useStore(runtime.status);
    const showWorkbench = useStore(workbenchStore.showWorkbench);
    // Parked in the build queue: accepted and waiting for a turn slot, not an error.
    const isQueued = useStore($isQueued);
    const isSleeping = chatStarted && sandboxStatus === 'sleeping';
    // Show the retry banner on the edit page (chatStarted) AND on the hero when a
    // fresh-chat boot just failed (sandboxBootFailed) — otherwise that path was
    // toast-only with no recoverable UI (DEV-146).
    const hasSandboxError = (chatStarted || sandboxBootFailed) && sandboxStatus === 'error';

    // DEV-155 readiness watchdog. 'starting' means the socket joined but the dev
    // server hasn't confirmed serving — expected while a generation is running
    // (the agent may still be scaffolding + booting the server), so we NEVER time
    // out mid-stream. It becomes a problem only when a generation FINISHES with
    // the pod still 'starting': the server likely never came up and the user is
    // stranded on a preview with no Retry.
    //
    // We arm on the generation-COMPLETION event (streaming true→false) — NOT a
    // persistent flag — so it can only ever fire for that exact case. A
    // resume/reconnect or a chat switch produces no such transition, and each
    // arming is scoped to the chat + boot cycle that just generated (a later chat
    // on the same mount can't inherit a stale "already generated" bit). We watch
    // the NARROW streamingState (a real generation), not the broad isStreaming
    // prop (which also covers isRestoring/fakeLoading). After a grace window we do
    // one final preview-url probe: a slow-but-healthy server is promoted to
    // 'running'; otherwise we surface the recoverable 'error' so
    // SandboxErrorBanner + Retry render. Stands down the instant serving is
    // confirmed (status leaves 'starting'), the chat switches, or a new
    // generation starts.
    const isGenerationStreaming = useStore(streamingState);
    const prevGenStreamingRef = useRef(isGenerationStreaming);
    useEffect(() => {
      const justCompleted = prevGenStreamingRef.current && !isGenerationStreaming;
      prevGenStreamingRef.current = isGenerationStreaming;

      if (!justCompleted || runtime.status.get() !== 'starting') {
        return undefined;
      }

      const chatIdAtArm = runtime.getChatId();

      // Mutable holder so `finish()` can clear a timer / unsubscribe that are
      // assigned below it, without a forward `let` reference.
      const w: { settled: boolean; timer?: ReturnType<typeof setTimeout>; unlisten: () => void } = {
        settled: false,
        unlisten: () => undefined,
      };

      const finish = () => {
        w.settled = true;

        if (w.timer) {
          clearTimeout(w.timer);
        }

        w.unlisten();
      };

      // Serving confirmed / pod slept / errored / chat switched → status leaves
      // 'starting', so there's nothing to warn about.
      w.unlisten = runtime.status.listen((next) => {
        if (next !== 'starting') {
          finish();
        }
      });

      w.timer = setTimeout(async () => {
        if (
          w.settled ||
          runtime.status.get() !== 'starting' ||
          runtime.getChatId() !== chatIdAtArm ||
          streamingState.get()
        ) {
          finish();

          return;
        }

        // Final confirmation before declaring failure — also gives a genuinely
        // dead pod a server-side recovery attempt (ensurePreviewReady). Retried
        // with backoff: a cold pod's wrangler-migrate + vite chain can outlast a
        // single recovery window, and the server's 503 cooldown makes one-shot
        // probes land on a bounce — a single probe here showed users "workspace
        // failed to start" over a pod that served seconds later (2026-08-01).
        const isCancelled = () =>
          w.settled ||
          runtime.getChatId() !== chatIdAtArm ||
          runtime.status.get() !== 'starting' ||
          Boolean(streamingState.get());

        const url = await fetchPreviewUrlWithRetry(
          async () => {
            const token = $accessToken.get();
            const res = await fetch(`/api/sandbox/${chatIdAtArm}/preview-url`, {
              credentials: 'include',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            return res.ok ? ((await res.json().catch(() => null))?.url ?? `/p/${chatIdAtArm}/`) : null;
          },
          { attempts: 3, initialDelayMs: 20_000, maxDelayMs: 20_000, isCancelled },
        );
        const serving = url !== null;

        finish();

        // Re-check the world didn't move on while the probe was in flight.
        if (runtime.getChatId() !== chatIdAtArm || runtime.status.get() !== 'starting' || streamingState.get()) {
          return;
        }

        if (serving) {
          runtime.status.set('running');
        } else {
          console.warn('[Sandbox] Dev server never served after generation — surfacing recoverable error (DEV-155)');
          runtime.status.set('error');
        }
      }, STARTING_READINESS_TIMEOUT_MS);

      return () => finish();
    }, [isGenerationStreaming]);

    const isWakingUp = useStore($wakingUp);
    const expoUrl = useStore(expoUrlAtom);
    const currentUser = useStore($currentUser);
    const [qrModalOpen, setQrModalOpen] = useState(false);

    // First-name (or fallback) for the empty-state welcome line. Never hardcode.
    const welcomeName = (() => {
      const dn = currentUser?.displayName?.trim();

      if (!dn) {
        return null;
      }

      const first = dn.split(/\s+/)[0];

      return first || null;
    })();

    useEffect(() => {
      if (expoUrl) {
        setQrModalOpen(true);
      }
    }, [expoUrl]);

    useEffect(() => {
      onStreamingChange?.(isStreaming);
    }, [isStreaming, onStreamingChange]);

    useEffect(() => {
      if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0])
            .map((result) => result.transcript)
            .join('');

          setTranscript(transcript);

          if (handleInputChange) {
            const syntheticEvent = {
              target: { value: transcript },
            } as React.ChangeEvent<HTMLTextAreaElement>;
            handleInputChange(syntheticEvent);
          }
        };

        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);

          if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
            setVoiceError('Microphone access is blocked — allow it in your browser settings.');
          } else if (event.error === 'audio-capture') {
            setVoiceError('No microphone found — check that one is connected.');
          } else if (event.error === 'network') {
            // Chrome/Edge stream audio to a cloud speech service; browsers that
            // block it (e.g. Brave) fail with 'network' immediately on start.
            setVoiceError("Can't reach the speech service — voice input needs Chrome or Edge with internet access.");
          } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
            setVoiceError(`Voice input stopped (${event.error}) — tap the mic to try again.`);
          }
        };

        // The browser can end recognition on its own (silence, network) — keep
        // the UI in sync so the mic never claims to be listening when it isn't.
        recognition.onend = () => {
          setIsListening(false);
        };

        setRecognition(recognition);
      }
    }, []);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        setIsModelLoading('all');

        const token = $accessToken.get();
        fetch('/api/models', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
          .then((response) => response.json())
          .then((data) => {
            const typedData = data as { modelList: ModelInfo[] };
            setModelList(typedData.modelList);
          })
          .catch((error) => {
            console.error('Error fetching model list:', error);
          })
          .finally(() => {
            setIsModelLoading(undefined);
          });
      }
    }, [providerList, provider]);

    const startListening = () => {
      if (!recognition) {
        setVoiceError("Voice input isn't supported in this browser — try Chrome or Edge.");
        return;
      }

      // Brave exposes the SpeechRecognition API but ships no speech backend, so
      // start() always dies with a 'network' error — tell the user upfront instead.
      if ((navigator as any).brave) {
        setVoiceError("Voice input isn't supported in Brave — try Chrome or Edge.");
        return;
      }

      setVoiceError(null);

      try {
        recognition.start();
        setIsListening(true);
      } catch (err) {
        if ((err as DOMException)?.name === 'InvalidStateError') {
          // already running — state is simply re-synced
          setIsListening(true);
        } else {
          console.error('Speech recognition failed to start:', err);
          setVoiceError('Voice input failed to start — tap the mic to try again.');
          setIsListening(false);
        }
      }
    };

    const stopListening = () => {
      if (recognition) {
        recognition.stop();
        setIsListening(false);
      }
    };

    const handleSendMessage = (event: React.UIEvent, messageInput?: string) => {
      if (sendMessage) {
        sendMessage(event, messageInput);

        if (recognition) {
          recognition.abort(); // Stop current recognition
          setTranscript(''); // Clear transcript
          setIsListening(false);

          // Clear the input by triggering handleInputChange with empty value
          if (handleInputChange) {
            const syntheticEvent = {
              target: { value: '' },
            } as React.ChangeEvent<HTMLTextAreaElement>;
            handleInputChange(syntheticEvent);
          }
        }
      }
    };

    // Read a file into the composer's image-attachment arrays (base64 preview +
    // raw File). Shared by the file picker and the homepage intent flow, which
    // passes an already-picked file so it can route by type first.
    const readImageAttachment = (file: File) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64Image = e.target?.result as string;
        setUploadedFiles?.([...uploadedFiles, file]);
        setImageDataList?.([...imageDataList, base64Image]);
      };
      reader.readAsDataURL(file);
    };

    const handleFileUpload = (providedFile?: File) => {
      if (providedFile) {
        readImageAttachment(providedFile);
        return;
      }

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];

        if (file) {
          readImageAttachment(file);
        }
      };

      input.click();
    };

    const handlePaste = async (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;

      if (!items) {
        return;
      }

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();

          const file = item.getAsFile();

          if (file) {
            const reader = new FileReader();

            reader.onload = (e) => {
              const base64Image = e.target?.result as string;
              setUploadedFiles?.([...uploadedFiles, file]);
              setImageDataList?.([...imageDataList, base64Image]);
            };
            reader.readAsDataURL(file);
          }

          break;
        }
      }
    };

    // Next-step chips from the last finished assistant reply (see suggestion-chips.ts).
    const suggestionItems = useMemo(() => latestSuggestions(messages, isStreaming), [messages, isStreaming]);

    // A chip loads its prompt into the composer, so the row has to outlive the
    // click: hiding on any non-empty input would take the other chips away the
    // moment you picked one, with no way back except clearing the box. The flag
    // keeps them up while a chip's text is being edited, and drops back to the
    // "empty composer only" rule once that text is sent or cleared.
    const [pickedFromChip, setPickedFromChip] = useState(false);

    useEffect(() => {
      if (!input.trim()) {
        setPickedFromChip(false);
      }
    }, [input]);

    // Extract the latest task list from all messages (metadata or annotations)
    const latestTasks: TaskItem[] = (() => {
      if (!messages) {
        return [];
      }

      // Only surface the plan for the CURRENT turn. Walk back from the newest
      // message but STOP at the latest user message — anything before it belongs to
      // a previous turn. Without this, sending a new message flashes the previous
      // turn's completed plan (e.g. "4/4 done") during "Generating…" until the new
      // plan is created. Now a new turn shows no plan until its own taskUpdate lands.
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i] as any;

        if (msg.role === 'user') {
          break;
        }

        const tasks = latestTaskUpdate(msg.metadata, msg.annotations);

        if (tasks.length > 0) {
          return tasks;
        }
      }

      return [];
    })();

    /*
     * The composer and everything that can replace it (sleep / queue / error
     * banners, the live build activity). Shared by both states: on the hero it
     * sits in the centred stack, mid-chat it docks to the bottom of the thread.
     */
    const composerStack = (
      <>
        {isSleeping || isWakingUp ? (
          <SleepBanner />
        ) : isQueued ? (
          /* Ahead of the sandbox-error branch on purpose: a queued build
             hasn't started, so any stale boot error from a previous
             attempt is not what the user is waiting on. */
          <BuildQueueBanner />
        ) : hasSandboxError ? (
          <SandboxErrorBanner />
        ) : isStreaming && chatStarted ? (
          <BuildActivity tasks={latestTasks} progress={progressAnnotations} handleStop={handleStop} />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {actionAlert && (
                <ChatAlert
                  alert={actionAlert}
                  clearAlert={() => clearAlert?.()}
                  postMessage={(message) => {
                    sendMessage?.({} as any, message);
                    clearAlert?.();
                  }}
                />
              )}
              {llmErrorAlert && (
                <LlmErrorAlert
                  alert={llmErrorAlert}
                  clearAlert={() => clearLlmErrorAlert?.()}
                  onRetry={onRetryLlmError}
                  onContinue={handleResume}
                />
              )}
            </div>
            {progressAnnotations && <ProgressCompilation data={progressAnnotations} />}
            {chatStarted && suggestionItems.length > 0 && (!input.trim() || pickedFromChip) && (
              <SuggestionChips
                items={suggestionItems}
                onPick={(item) => {
                  // Loaded, not sent: a chip is a starting point, so the user
                  // gets to edit the prompt before it goes anywhere.
                  handleInputChange?.({
                    target: { value: item.prompt },
                  } as React.ChangeEvent<HTMLTextAreaElement>);
                  setPickedFromChip(true);
                  textareaRef?.current?.focus();
                }}
              />
            )}
            {/* No plan card above the composer once the turn is done — the live
                plan lives in BuildActivity during the build, and lingering it
                here (often a stale "0/N done") is just noise. */}
            <ChatBox
              isModelSettingsCollapsed={isModelSettingsCollapsed}
              setIsModelSettingsCollapsed={setIsModelSettingsCollapsed}
              provider={provider}
              setProvider={setProvider}
              providerList={providerList || (PROVIDER_LIST as ProviderInfo[])}
              model={model}
              setModel={setModel}
              modelList={modelList}
              isModelLoading={isModelLoading}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              imageDataList={imageDataList}
              setImageDataList={setImageDataList}
              textareaRef={textareaRef}
              input={input}
              handleInputChange={handleInputChange}
              handlePaste={handlePaste}
              TEXTAREA_MIN_HEIGHT={chatStarted ? TEXTAREA_MIN_HEIGHT : TEXTAREA_MIN_HEIGHT_NEW}
              TEXTAREA_MAX_HEIGHT={TEXTAREA_MAX_HEIGHT}
              isStreaming={isStreaming}
              handleStop={handleStop}
              canResume={canResume}
              handleResume={handleResume}
              handleSendMessage={handleSendMessage}
              enhancingPrompt={enhancingPrompt}
              enhancePrompt={enhancePrompt}
              composerError={enhanceError ?? voiceError}
              onClearComposerError={() => {
                clearEnhanceError?.();
                setVoiceError(null);
              }}
              isListening={isListening}
              startListening={startListening}
              stopListening={stopListening}
              chatStarted={chatStarted}
              exportChat={exportChat}
              qrModalOpen={qrModalOpen}
              setQrModalOpen={setQrModalOpen}
              handleFileUpload={handleFileUpload}
              chatMode={chatMode}
              setChatMode={setChatMode}
              designScheme={designScheme}
              setDesignScheme={setDesignScheme}
              projectType={projectType}
              setProjectType={setProjectType}
              isStartingChat={isStartingChat}
              addToProject={addToProject}
              onToggleAddToProject={onToggleAddToProject}
              onRemoveFile={onRemoveFile}
              brochure={brochure}
              onBrochureAttach={onBrochureAttach}
              onBrochureRemove={onBrochureRemove}
              pdfAttachments={pdfAttachments}
              onPdfAttach={onPdfAttach}
              onPdfAttachRemove={onPdfAttachRemove}
            />
            {!chatStarted && (
              <>
                <ExamplePrompts
                  onSelect={(prompt) => {
                    sendMessage?.({} as React.UIEvent, prompt);
                  }}
                />
                <BrochureHint onPick={requestComposerUpload} />
              </>
            )}
          </>
        )}
      </>
    );

    const baseChat = (
      <div
        ref={ref}
        className={classNames(
          styles.BaseChat,
          'ui-root relative flex h-full w-full bg-page',
          chatStarted ? 'flex-col overflow-hidden' : 'overflow-y-auto modern-scrollbar',
        )}
        data-chat-visible={showChat}
        onDragOver={
          chatStarted
            ? undefined
            : (event) => {
                if (event.dataTransfer.types.includes('Files')) {
                  event.preventDefault();
                  setIsDraggingFile(true);
                }
              }
        }
        onDragLeave={
          chatStarted
            ? undefined
            : (event) => {
                // Only when the pointer actually leaves the hero, not on every
                // child boundary crossing.
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setIsDraggingFile(false);
                }
              }
        }
        onDrop={
          chatStarted
            ? undefined
            : (event) => {
                const file = event.dataTransfer.files?.[0];

                if (!file) {
                  return;
                }

                event.preventDefault();
                setIsDraggingFile(false);
                submitComposerFile(file);
              }
        }
      >
        {!chatStarted && (
          // Subtle lime accent radial glow at bottom of hero — on-brand atmosphere
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background: 'radial-gradient(ellipse 60% 40% at 50% 100%, rgba(217,242,106,0.08) 0%, transparent 60%)',
            }}
          />
        )}
        {chatStarted && <Header />}
        <div
          className={classNames(
            'relative z-[1] flex flex-col lg:flex-row w-full',
            chatStarted ? 'flex-1 min-h-0' : 'min-h-full',
          )}
        >
          <div
            className={classNames(
              styles.Chat,
              'flex flex-col relative',
              chatStarted ? 'h-full overflow-hidden' : 'min-h-full',
              chatStarted
                ? showWorkbench
                  ? `lg:w-[var(--chat-split-percent)] lg:flex-shrink-0 ${showChat ? 'lg:min-w-[var(--chat-min-width)]' : 'lg:min-w-0'}`
                  : // Preview closed → chat expands to full width.
                    'lg:w-full flex-1 min-w-0'
                : 'flex-grow min-w-0',
            )}
            style={
              // Preview closed → widen the shared chat measure so the thread AND the
              // composer (both keyed off --chat-max-width) expand together, aligned.
              chatStarted && !showWorkbench ? ({ '--chat-max-width': '52rem' } as React.CSSProperties) : undefined
            }
          >
            {chatStarted ? (
              <StickToBottom
                className="relative flex h-full flex-col px-4 pt-6 modern-scrollbar sm:px-8"
                resize="smooth"
                initial="smooth"
              >
                <StickToBottom.Content className="relative flex flex-col overflow-x-hidden">
                  <Messages
                    className="z-1 mx-auto flex w-full max-w-chat flex-1 min-w-0 flex-col pb-4"
                    messages={messages}
                    isStreaming={isStreaming}
                    append={append}
                    chatMode={chatMode}
                    setChatMode={setChatMode}
                    provider={provider}
                    model={model}
                    checkpoints={checkpoints}
                    onRestore={onRestore}
                  />
                  <ScrollToBottom />
                </StickToBottom.Content>
                {/* `mt-auto` consumes the free space above the composer, pinning it
                    to the bottom of the flex-col scroll container. Without it the
                    composer sat right after the messages, so toggling the inline
                    plan panel (TaskProgress) — which changes message height — shifted
                    it. `sticky bottom-2` keeps it visible once the chat scrolls.
                    Phones get a tighter bottom gap that still clears the home bar. */}
                <div className="z-prompt sticky bottom-2 mx-auto mb-[max(0.75rem,env(safe-area-inset-bottom))] mt-auto flex w-full max-w-chat flex-col gap-2 sm:mb-6">
                  {composerStack}
                </div>
              </StickToBottom>
            ) : (
              <>
                {/* Animated empty-state atmosphere (DESIGN.md §3) — grid + breathing
                    glow + drifting aurora + twinkling stars, behind the hero. */}
                <HeroBackground />

                {/* One centred stack, one measure. `my-auto` centres it while it
                    is shorter than the viewport and simply yields once the recent
                    grid makes it taller — so there is never a screen of dead
                    black under the fold, at any height. */}
                <div className="relative z-10 mx-auto my-auto w-full max-w-chat px-4 py-10 sm:px-6 sm:py-14">
                  <motion.div
                    id="intro"
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  >
                    <motion.div
                      className="ui-caption mb-4 inline-block sm:mb-5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.15, duration: 0.4 }}
                    >
                      {welcomeName ? `Welcome back, ${welcomeName}` : 'Welcome'}
                    </motion.div>
                    <motion.h1
                      className="ui-serif mb-3.5 text-balance text-[32px] leading-[1.06] text-primary sm:text-[44px] lg:text-[56px]"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.55, ease: 'easeOut' }}
                    >
                      What are we <span className="italic text-accent">building</span> today?
                    </motion.h1>
                    <motion.p
                      className="mx-auto max-w-[38ch] text-balance font-geist text-[13.5px] leading-[1.6] text-secondary sm:max-w-[46ch] sm:text-[14.5px]"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.45 }}
                    >
                      Describe it in a sentence. Dunea designs it, builds it, and puts it on a real URL.
                    </motion.p>
                  </motion.div>

                  <div className="mt-7 flex flex-col gap-2 sm:mt-9">{composerStack}</div>

                  <RecentProjects
                    emptyFallback={
                      <ShowcasePrompts
                        onUsePrompt={(prompt) => {
                          // Loaded, not sent: these are somebody else's business,
                          // so the user should edit before building.
                          handleInputChange?.({
                            target: { value: prompt },
                          } as React.ChangeEvent<HTMLTextAreaElement>);
                          textareaRef?.current?.focus();
                        }}
                      />
                    }
                  />
                </div>

                {/* Drop anywhere on the hero. Brochure-to-site is the capability
                    users never found; a page-wide target is the cheapest way to
                    let them discover it by instinct. */}
                <AnimatePresence>
                  {isDraggingFile && (
                    <motion.div
                      className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-page/80 backdrop-blur-[2px]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.12 }}
                    >
                      <div className="mx-4 flex max-w-sm flex-col items-center gap-2.5 rounded-2xl border border-dashed border-accent/50 bg-surface px-8 py-9 text-center">
                        <span className="i-ph:file-arrow-down text-[26px] text-accent" />
                        <p className="m-0 font-geist text-[14px] font-medium text-primary">Drop it here</p>
                        <p className="m-0 font-geist text-[12.5px] leading-relaxed text-muted">
                          A brochure or deck becomes a site that matches its brand. An image becomes a reference.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
          {chatStarted && <Workbench chatStarted={chatStarted} isStreaming={isStreaming} />}
        </div>
      </div>
    );

    return <Tooltip.Provider delayDuration={200}>{baseChat}</Tooltip.Provider>;
  },
);

/**
 * The second way in. Brochure-to-site is the most distinctive thing Dunea does
 * and it lived behind an unlabelled paperclip, so nobody knew it existed. It is
 * also a rare input compared to typing, which is why it gets one quiet line
 * under the composer rather than half the hero.
 */
function BrochureHint({ onPick }: { onPick: () => void }) {
  return (
    <div className="mt-5 px-1 text-center sm:mt-6">
      <p className="m-0 font-geist text-[12.5px] leading-relaxed text-muted">
        <span className="i-ph:file-pdf mr-1.5 inline-block translate-y-[2px] text-[13px] text-faint" />
        Have a brochure or a deck?{' '}
        <button
          type="button"
          onClick={onPick}
          className="cursor-pointer border-0 bg-transparent p-0 font-geist text-[12.5px] font-medium text-accent underline decoration-accent/30 underline-offset-[3px] transition-colors hover:decoration-accent"
        >
          Start from the PDF
        </button>{' '}
        and we match its brand.
      </p>
    </div>
  );
}

function ScrollToBottom() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  return (
    !isAtBottom && (
      <>
        <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-page via-page/60 to-transparent h-12 z-10 pointer-events-none" />
        <button
          className="sticky z-50 bottom-4 left-0 right-0 flex items-center justify-center gap-1.5 mx-auto px-3 py-1.5 rounded-full bg-raised/95 backdrop-blur-md border border-subtle text-secondary text-[11px] font-geist hover:text-primary hover:border-prominent transition-colors cursor-pointer touch:min-h-9 touch:px-4"
          onClick={() => scrollToBottom()}
        >
          <span className="i-ph:arrow-down text-[12px]" />
          Jump to bottom
        </button>
      </>
    )
  );
}
